'use client';

import React, { Fragment, useState } from 'react';
import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { extractComponentFromChunk } from '@/ui/utils/tiles';
import { twJoin, twMerge } from 'tailwind-merge';
import {
  ContentChunkFragment,
  RichTextFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import dynamic from 'next/dynamic';
import { buildTrackingId } from '@/utils/tracking-helper';
import Container from '../../shared/Container';
import { RichTextRenderer } from '../../tileRenderers/RichTextRenderer';
import OptimizedImage from '../../shared/OptimizedImage';

export type AccordionTabSwitcherTile = {
  tabs: ContentChunkFragment | null;
};

// Importing dynamicly fixed the Hydration Mismatch error
const ReactPlayer = dynamic(() => import('react-player'));

function isRichtextEmpty(blocks: RichTextFragment | undefined) {
  if (!blocks) {
    return true;
  }
  return (
    blocks?.json?.[0]?.type === 'paragraph' &&
    blocks?.json?.[0]?.children === undefined &&
    blocks?.json?.[0]?.textContent === undefined
  );
}

export default function AccordionTabSwitcher({
  tile,
  trackingPrefix,
}: Readonly<{
  tile: AccordionTabSwitcherTile;
  trackingPrefix?: string;
}>) {
  const [activeTab, setActiveTab] = useState(0);
  const chunks = tile.tabs?.chunks;
  if (!chunks) {
    return null;
  }

  const firstIcon = extractComponentFromChunk(chunks[0], 'icon', 'ImageContent');

  const buildTabTrackingId = (titlePlainText: string | undefined, index: number) =>
    buildTrackingId(trackingPrefix, titlePlainText || `tab-${index}`);

  return (
    <Container>
      <div className="gap-x-8 lg:grid lg:grid-cols-2">
        <div>
          {chunks.map((chunk, index: number) => {
            const icon = extractComponentFromChunk(chunk, 'icon', 'ImageContent');
            const title = extractComponentFromChunk(chunk, 'title', 'RichTextContent');
            const description = extractComponentFromChunk(chunk, 'description', 'RichTextContent');
            const activeTabText = extractComponentFromChunk(
              chunk,
              'active-tab-text',
              'RichTextContent',
            );
            const chunkImage = extractComponentFromChunk(chunk, 'image', 'ImageContent');
            const chunkVideo = extractComponentFromChunk(chunk, 'video', 'VideoContent')
              ?.videos?.[0];
            const tabTrackingId = buildTabTrackingId(title?.plainText?.[0], index);

            return (
              <Fragment key={`desktop-tab-${index}`}>
                {/* // Desktop view */}
                <button
                  type="button"
                  aria-label={title?.plainText?.[0] || `Tab ${index + 1}`}
                  onClick={() => setActiveTab(index)}
                  className={twMerge(
                    'hidden w-full grid-cols-[auto_1fr] gap-x-4 rounded-xl pb-6 pl-6 pr-25 pt-6 text-left duration-100 ease-in hover:bg-neutral-5 lg:mb-4 lg:grid',
                    index === activeTab && 'pointer-events-none bg-neutral-5',
                  )}
                  data-tile-element-id={tabTrackingId}
                >
                  <OptimizedImage
                    width={50}
                    height={50}
                    imageComponent={icon || firstIcon}
                    className=" my-auto h-12 w-auto"
                    sizes="50px"
                    dataTestId="icon"
                  />
                  <div className="my-auto space-y-2">
                    <RichTextRenderer blocks={title} className="text-2xl" />
                    <RichTextRenderer blocks={description} className="text-md" />
                  </div>
                  {index === activeTab && !isRichtextEmpty(activeTabText) && (
                    <>
                      <div />
                      <div className="mt-3">
                        <RichTextRenderer blocks={activeTabText} className="text-md" />
                      </div>
                    </>
                  )}
                </button>

                {/* // Mobile view */}
                <Disclosure>
                  {({ open }) => (
                    <div className=" mb-3 lg:hidden" key={`mobile-tab-${index}`}>
                      <Disclosure.Button
                        className={twJoin(
                          'relative grid w-full grid-cols-[auto_1fr] gap-x-4 bg-neutral-5 p-4 py-6 text-left duration-200 ease-in sm:py-8',
                          open ? 'rounded-t-xl' : ' rounded-xl',
                        )}
                        data-tile-element-id={tabTrackingId}
                      >
                        <OptimizedImage
                          width={50}
                          height={50}
                          imageComponent={icon}
                          className=" my-auto h-12 w-auto"
                          sizes="50px"
                          dataTestId="icon"
                        />
                        <div className="my-auto space-y-1 sm:pr-10">
                          <RichTextRenderer blocks={title} className="text-lg sm:text-xl" />
                          <RichTextRenderer blocks={description} className="md:text-md" />
                        </div>
                        {open && (
                          <>
                            <div />
                            <div className="mt-2">
                              <RichTextRenderer blocks={activeTabText} className="md:text-md" />
                            </div>
                          </>
                        )}
                        <ChevronDownIcon
                          className={twJoin(
                            'absolute right-2 top-2 h-auto w-6 text-neutral-50 duration-200 ease-in sm:mr-2',
                            !open && 'rotate-180 transform',
                          )}
                        />
                      </Disclosure.Button>
                      <Disclosure.Panel className="relative h-xs w-full rounded-b-xl sm:h-sm">
                        <OptimizedImage
                          fill
                          className="rounded-b-xl object-cover"
                          imageComponent={chunkImage}
                          sizes="100vw"
                        />
                        {chunkVideo && (
                          <ReactPlayer
                            url={chunkVideo.playlists?.[1]}
                            playing
                            loop
                            muted
                            width="100%"
                            height="100%"
                            className="video-cover absolute inset-0 overflow-hidden rounded-b-xl"
                            playsinline
                          />
                        )}
                      </Disclosure.Panel>
                    </div>
                  )}
                </Disclosure>
              </Fragment>
            );
          })}
        </div>
        <div className="relative hidden aspect-[4/3] h-auto w-full lg:block">
          {chunks.map((chunk, index: number) => {
            const chunkImage = extractComponentFromChunk(chunk, 'image', 'ImageContent');
            const chunkVideo = extractComponentFromChunk(chunk, 'video', 'VideoContent')
              ?.videos?.[0];

            if (chunkImage) {
              return (
                <OptimizedImage
                  key={`image-accordion-${index}`}
                  fill
                  className={twJoin(
                    'absolute inset-0 rounded-xl object-cover',
                    index === activeTab ? 'opacity-100' : 'opacity-0',
                  )}
                  imageComponent={chunkImage}
                  sizes="50vw"
                  dataTestId="current-image"
                />
              );
            }

            if (chunkVideo) {
              return (
                <ReactPlayer
                  url={chunkVideo.playlists?.[1]}
                  playing={index === activeTab}
                  loop
                  muted
                  width="100%"
                  height="100%"
                  className="video-cover absolute inset-0 overflow-hidden rounded-xl"
                  style={index === activeTab ? { opacity: 1 } : { opacity: 0 }}
                  playsinline
                />
              );
            }
          })}
        </div>
      </div>
    </Container>
  );
}
